import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Camera, ImageOff, RotateCcw, Sparkles, Upload, Wand2 } from "lucide-react";
import {
  MAX_IMAGE_BYTES,
  checkImageSize,
  sniffImageType,
  type ImageRejection,
} from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useAssets,
  useEnhanceAsset,
  usePostFromAsset,
  useRevertAsset,
  useUploadPhoto,
  type MediaAsset,
} from "@/lib/media";
import { PagePicker } from "@/components/PagePicker";
import { ErrorBox } from "./LoginPage";

/**
 * Photo → post (spec §6 flow 2, phase 8).
 *
 * Three steps, in the order the owner actually works: take the photo, say
 * what it is, let us tidy it up. Captions are a separate button because the
 * owner should decide the image is right before we spend tokens writing for
 * it — and because the enhancement is the part most likely to need a second
 * look.
 *
 * The before/after is shown side by side rather than as a replacement. The
 * enhancement prompt forbids altering the product, but an owner who can only
 * see the result has no way to tell whether it obeyed.
 */

type LocalCheck = { ok: true } | { ok: false; reason: ImageRejection["reason"] };

/**
 * The same verdict the server will reach, reached here first.
 *
 * Size comes from the File's own metadata and the type from its first 16
 * bytes, so a 40 MB photo or a PDF is refused without reading or uploading
 * the whole thing. The server repeats both checks — this is to save the owner
 * a wasted upload, not to be trusted.
 *
 * Only the reason is returned, not the shared module's message: those are
 * English, written for API responses, and this screen is translated.
 */
async function checkLocally(file: File): Promise<LocalCheck> {
  const sizeProblem = checkImageSize(file.size);
  if (sizeProblem) return { ok: false, reason: sizeProblem.reason };

  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  if (!sniffImageType(head)) return { ok: false, reason: "unsupported_type" };
  return { ok: true };
}

export function PhotoPostPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId && brands.data?.length) setBrandId(brands.data[0]!.id);
  }, [brands.data, brandId]);

  const assets = useAssets(brandId);
  const upload = useUploadPhoto();
  const enhance = useEnhanceAsset();
  const revert = useRevertAsset();
  const writeCaptions = usePostFromAsset();

  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [wantsEnhance, setWantsEnhance] = useState(true);
  const [asset, setAsset] = useState<MediaAsset | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  /** Empty = every connected page for this brand. */
  const [pages, setPages] = useState<string[]>([]);

  // Object URLs are revoked on replace and on unmount; without this every
  // photo the owner previews stays held in memory for the life of the tab.
  useEffect(() => {
    if (!file) return setPreviewUrl(null);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const busy = upload.isPending || enhance.isPending || writeCaptions.isPending;

  function reset() {
    setFile(null);
    setAsset(null);
    setSubject("");
    setNotice(null);
    setError(null);
    setDone(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function choose(next: File | null) {
    setError(null);
    setNotice(null);
    setAsset(null);
    setDone(false);
    if (!next) return setFile(null);

    // The same check the server runs, so an unusable file is refused here
    // instead of after a 6 MB upload.
    const check = await checkLocally(next);
    if (!check.ok) {
      setFile(null);
      setError(
        t(`photo.reject.${check.reason}`, {
          mb: (next.size / (1024 * 1024)).toFixed(1),
          limit: MAX_IMAGE_BYTES / (1024 * 1024),
        }),
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    setFile(next);
  }

  async function submit() {
    if (!brandId || !file) return;
    setError(null);
    setNotice(null);
    try {
      const result = await upload.mutateAsync({
        brandId,
        subject: subject.trim(),
        enhance: wantsEnhance,
        file,
      });
      setAsset(result.asset);
      // The upload succeeded either way; this says only that the tidy-up
      // didn't, and the original is still usable.
      setNotice(result.enhanceError ? t("photo.enhanceFailed") : null);
    } catch (err) {
      setError(apiErrorMessage(err, t("photo.uploadFailed")));
    }
  }

  async function runEnhance(target: MediaAsset) {
    setError(null);
    setNotice(null);
    try {
      const result = await enhance.mutateAsync({ assetId: target.id, subject: subject.trim() || target.prompt || "" });
      setAsset(result.asset);
      setNotice(result.enhanceError ? t("photo.enhanceFailed") : null);
    } catch (err) {
      setError(apiErrorMessage(err, t("photo.enhanceFailed")));
    }
  }

  async function useOriginal(target: MediaAsset) {
    setError(null);
    try {
      setAsset(await revert.mutateAsync(target.id));
      setNotice(t("photo.usingOriginal"));
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }

  async function createPost(target: MediaAsset) {
    setError(null);
    try {
      await writeCaptions.mutateAsync({
        assetId: target.id,
        topic: subject.trim() || undefined,
        socialAccountIds: pages,
      });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, t("photo.captionsFailed")));
    }
  }

  const canUpload = !!brandId && !!file && subject.trim().length >= 3 && !busy;
  const shown = asset?.enhancedUrl ?? asset?.originalUrl ?? previewUrl;

  const recent = useMemo(
    () => (assets.data ?? []).filter((a) => a.id !== asset?.id),
    [assets.data, asset?.id],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-ink">
            <Camera size={18} className="text-primary-600" />
            {t("photo.title")}
          </h1>
          <p className="mt-1 text-sm text-muted">{t("photo.subtitle")}</p>
        </div>
        <select
          value={brandId ?? ""}
          onChange={(e) => {
            setBrandId(e.target.value || null);
            reset();
          }}
          className="select"
        >
          {(brands.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </header>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      {done ? (
        <section className="card-pad">
          <p className="text-sm">
            {t("photo.draftCreated")}{" "}
            <Link to="/calendar" className="text-primary-600 hover:underline">
              {t("photo.openCalendar")}
            </Link>
          </p>
          <button
            onClick={reset}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-gray-50"
          >
            <Camera size={14} /> {t("photo.another")}
          </button>
        </section>
      ) : (
        <section className="grid gap-4 rounded-xl border border-border bg-white p-4 md:grid-cols-2">
          {/* ── Step 1: the photo ── */}
          <div className="space-y-3">
            <label className="block text-sm font-medium">{t("photo.stepPhoto")}</label>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                void choose(e.dataTransfer.files?.[0] ?? null);
              }}
              className="flex min-h-48 items-center justify-center rounded-lg border-2 border-dashed border-border p-3"
            >
              {shown ? (
                <img src={shown} alt="" className="max-h-64 rounded-lg object-contain" />
              ) : (
                <button
                  onClick={() => fileInput.current?.click()}
                  className="flex flex-col items-center gap-2 text-sm text-muted hover:text-ink"
                >
                  <Upload size={22} />
                  {t("photo.dropHint")}
                </button>
              )}
            </div>

            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border file:border-border file:bg-white file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => void choose(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted">{t("photo.formats")}</p>
          </div>

          {/* ── Step 2: what it is, and whether to tidy it ── */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium">{t("photo.stepSubject")}</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("photo.subjectPlaceholder")}
                className="input mt-1"
              />
              <p className="mt-1 text-xs text-muted">{t("photo.subjectWhy")}</p>
            </div>

            {!asset ? (
              <>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={wantsEnhance}
                    onChange={(e) => setWantsEnhance(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    {t("photo.enhanceLabel")}
                    <span className="block text-xs text-muted">{t("photo.enhanceWhy")}</span>
                  </span>
                </label>

                <button
                  onClick={() => void submit()}
                  disabled={!canUpload}
                  className="btn-primary btn-sm"
                >
                  <Wand2 size={14} />
                  {upload.isPending
                    ? wantsEnhance
                      ? t("photo.enhancing")
                      : t("photo.uploading")
                    : t("photo.upload")}
                </button>
                {upload.isPending && wantsEnhance ? (
                  <p className="text-xs text-muted">{t("photo.enhancingWait")}</p>
                ) : null}
              </>
            ) : (
              <div className="space-y-3">
                {notice ? (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{notice}</p>
                ) : null}

                {asset.enhancedUrl ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted">{t("photo.compare")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <figure>
                        <img src={asset.originalUrl} alt="" className="rounded-lg border border-border" />
                        <figcaption className="mt-1 text-center text-xs text-muted">
                          {t("photo.yours")}
                        </figcaption>
                      </figure>
                      <figure>
                        <img src={asset.enhancedUrl} alt="" className="rounded-lg border border-primary-300" />
                        <figcaption className="mt-1 text-center text-xs text-primary-700">
                          {t("photo.improved")}
                        </figcaption>
                      </figure>
                    </div>
                    <p className="text-xs text-muted">{t("photo.checkProduct")}</p>
                    <button
                      onClick={() => void useOriginal(asset)}
                      disabled={revert.isPending}
                      className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline disabled:opacity-50"
                    >
                      <RotateCcw size={14} /> {t("photo.useMine")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => void runEnhance(asset)}
                    disabled={enhance.isPending || subject.trim().length < 3}
                    className="btn-secondary btn-sm"
                  >
                    <Sparkles size={14} />
                    {enhance.isPending ? t("photo.enhancing") : t("photo.enhanceNow")}
                  </button>
                )}

                {/* Which pages this post goes to. Hidden for a brand with one
                    page, where it would be a choice with no alternative. */}
                <div className="border-t border-border pt-3">
                  <PagePicker brandId={brandId} value={pages} onChange={setPages} />
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    onClick={() => void createPost(asset)}
                    disabled={writeCaptions.isPending}
                    className="btn-primary btn-sm"
                  >
                    <Sparkles size={14} />
                    {writeCaptions.isPending ? t("photo.writing") : t("photo.writeCaptions")}
                  </button>
                  <button
                    onClick={reset}
                    className="btn-ghost btn-sm"
                  >
                    {t("photo.startOver")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── The brand's photo library ── */}
      <section className="card-pad">
        <h2 className="text-sm font-medium">{t("photo.recent")}</h2>
        {recent.length === 0 ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-muted">
            <ImageOff size={14} /> {t("photo.noneYet")}
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {recent.map((a) => (
              <li key={a.id}>
                <button
                  onClick={() => {
                    setAsset(a);
                    setFile(null);
                    setSubject(a.prompt ?? "");
                    setDone(false);
                    setNotice(null);
                  }}
                  className="block w-full overflow-hidden rounded-lg border border-border hover:border-primary-400"
                  title={a.prompt ?? ""}
                >
                  <img
                    src={a.enhancedUrl ?? a.originalUrl}
                    alt=""
                    className="aspect-square w-full object-cover"
                  />
                </button>
                <p className="mt-1 truncate text-xs text-muted">{a.prompt ?? t("photo.untitled")}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
